import '@mui/x-data-grid/themeAugmentation';
import { Components, Theme } from '@mui/material/styles';

/** Shared table chrome for every product grid; column content stays local. */
export const MuiDataGrid: Components<Theme>['MuiDataGrid'] = {
  defaultProps: { rowHeight: 76, columnHeaderHeight: 44 },
  styleOverrides: {
    root: ({ theme }) => {
      const t = theme.palette.studio;
      return theme.unstable_sx({
        // Surface colors come from palette.DataGrid so MUI X's header filler
        // and scrollbars also blend into the page. Keep separators neutral.
        '&&': {
          '--DataGrid-t-color-border-base': t.borders.divider,
        },
        width: '100%',
        height: '100%',
        border: 0,
        borderRadius: 0,
        bgcolor: 'transparent',
        boxShadow: 'none',
        color: t.text.primary,
        '& .MuiDataGrid-toolbar': {
          minHeight: 56,
          flex: '0 0 auto',
          justifyContent: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 1.5,
          px: { xs: 1.5, sm: 2.5 },
          py: 1,
          bgcolor: 'transparent',
          borderBottom: `1px solid ${t.borders.divider}`,
        },
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: 'transparent',
          borderBottomColor: t.borders.divider,
        },
        '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
          px: 1.5,
        },
        '& .MuiDataGrid-columnHeader[data-field="name"], & .MuiDataGrid-cell[data-field="name"]':
          {
            pl: 2.5,
          },
        '& .MuiDataGrid-columnHeaderTitle': {
          color: 'text.secondary',
          fontSize: 11,
          textTransform: 'none',
          letterSpacing: '0.015em',
          fontWeight: 600,
        },
        '& .MuiDataGrid-columnSeparator': {
          opacity: 0,
          transition: 'opacity 150ms ease',
        },
        '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-columnSeparator':
          {
            opacity: 1,
          },
        '& .MuiDataGrid-cell': {
          userSelect: 'none',
          fontSize: 13,
          lineHeight: 1.4,
          display: 'flex',
          alignItems: 'center',
          borderBottom: 0,
          borderTop: `1px solid ${t.borders.hairline}`,
        },
        '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end' },
        '& .MuiDataGrid-cell--textCenter': { justifyContent: 'center' },
        // Pointer interaction should not look like spreadsheet selection.
        // Restore the outline below for keyboard-focused cells and headers.
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },
        '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible':
          {
            outline: `2px solid ${t.colors.accentText}`,
            outlineOffset: -2,
          },
        '& .MuiDataGrid-row': {
          bgcolor: 'transparent',
          boxShadow: 'none',
          '&:hover, &:focus-within': { bgcolor: t.surfaces.fillSubtle },
          '&:hover .project-icon, &:focus-within .project-icon': {
            color: t.text.primary,
            bgcolor: t.surfaces.fillActive,
          },
        },
        '& .MuiDataGrid-main': {
          contain: 'layout paint style',
        },
        '& .MuiDataGrid-footerContainer': {
          px: 2,
          overflowX: 'hidden',
          minHeight: 56,
          bgcolor: 'transparent',
          borderTopColor: t.borders.divider,
          color: t.text.secondary,
        },
        '& .MuiDataGrid-footerContainer .MuiTablePagination-root': {
          width: '100%',
          overflow: 'hidden',
        },
        '& .MuiDataGrid-footerContainer .MuiTablePagination-toolbar': {
          minHeight: 56,
          px: 0,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          columnGap: 1,
        },
        '& .MuiDataGrid-footerContainer .MuiTablePagination-spacer': {
          flex: '1 1 auto',
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select':
          {
            fontSize: 11,
            color: t.text.secondary,
            fontVariantNumeric: 'tabular-nums',
          },
        '& .MuiTablePagination-actions .MuiIconButton-root': {
          border: `1px solid ${t.borders.subtle}`,
          borderRadius: '8px',
          p: 0.5,
          ml: 0.75,
        },
        '& .MuiDataGrid-row:hover .row-chevron, & .MuiDataGrid-row:focus-within .row-chevron':
          {
            color: t.colors.accentText,
            bgcolor: t.status.syncing.bg,
          },
        '@media (prefers-reduced-motion: reduce)': {
          '& .MuiDataGrid-row, & .project-icon, & .row-chevron': {
            transition: 'none',
          },
        },
      });
    },
  },
};
